import React from 'react'
import Flatpickr from 'react-flatpickr'
import { Arabic } from 'flatpickr/dist/l10n/ar'
import { French } from 'flatpickr/dist/l10n/fr'
import { Calendar as CalendarIcon, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { dateToLocalDateString } from 'helpers/dateTime'
import { cn } from '../lib/utils'
import { normalizeAppLanguage } from 'helpers/documentLocale'

/** Parse YYYY-MM-DD as a local calendar date (avoids UTC midnight shift from `new Date(iso)`). */
function parseYmdLocal(ymd: string): Date {
 const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim())
 if (!m) return new Date(ymd)
 return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

export type DatePickerMode = 'single' | 'range'

type Props = {
 id?: string
 value: string | null
 onChange: (value: string | null) => void
 mode?: DatePickerMode
 placeholder?: string
 disabled?: boolean
 className?: string
 clearable?: boolean
 'aria-label'?: string
}

export function DatePicker({
 id,
 value,
 onChange,
 mode = 'single',
 placeholder,
 disabled,
 className,
 clearable = true,
 'aria-label': ariaLabel,
}: Props) {
 const { t, i18n } = useTranslation()
 const lang = normalizeAppLanguage(i18n.language)
 const rtl = lang === 'ar'
 const resolvedPlaceholder = placeholder ?? t('pos.datePicker.placeholder')

 const inputClass = cn(
 'flex h-10 min-h-10 w-full items-center rounded-xl border border-border-subtle bg-background px-3 py-2 text-sm shadow-sm',
 ' placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0',
 disabled && 'cursor-not-allowed ',
 className
 )

 const altInputClass = cn('flatpickr-alt-input', inputClass, rtl ? 'ps-10 text-start' : 'ps-10 text-left')

 const defaultDate = React.useMemo(() => {
 if (!value) return undefined
 if (mode === 'range') {
 const parts = value.split(/\s+to\s+|\s+-\s+/).map((s) => s.trim()).filter(Boolean)
 if (parts.length === 2) return [parseYmdLocal(parts[0]), parseYmdLocal(parts[1])]
 return undefined
 }
 return parseYmdLocal(value)
 }, [value, mode])

 const handleFlatpickrReady = React.useCallback(
 (_selectedDates: Date[], _dateStr: string, instance: { calendarContainer?: HTMLElement }) => {
 const el = instance.calendarContainer
 if (!el) return
 el.style.zIndex = '110000'
 el.style.pointerEvents = 'auto'
 },
 []
 )

 const handleChange = (dates: Date[]) => {
 if (!dates || dates.length === 0) {
 onChange(null)
 return
 }
 if (mode === 'range') {
 if (dates.length < 2) return
 const start = dateToLocalDateString(dates[0])
 const end = dateToLocalDateString(dates[1])
 onChange(`${start} to ${end}`)
 return
 }
 onChange(dateToLocalDateString(dates[0]))
 }

 return (
 <div className="relative" dir={rtl ? 'rtl' : 'ltr'}>
 <Flatpickr
 id={id}
 className={cn(inputClass, 'ps-10')}
 placeholder={resolvedPlaceholder}
 aria-label={ariaLabel}
 options={{
 disableMobile: true,
 mode: mode === 'range' ? 'range' : 'single',
 dateFormat: 'Y-m-d',
 altInput: true,
 altFormat: rtl ? 'j F Y' : lang === 'fr' ? 'd/m/Y' : 'Y-m-d',
 altInputClass,
 locale: rtl ? Arabic : lang === 'fr' ? French : undefined,
 onReady: handleFlatpickrReady,
 }}
 onChange={handleChange}
 value={defaultDate}
 disabled={disabled}
 />

 <CalendarIcon
 className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden
 />

 {clearable && value && !disabled ? (
 <button
 type="button" className="absolute end-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:text-foreground"
 onClick={() => onChange(null)}
 aria-label={t('pos.datePicker.clear')}>
 <X className="h-4 w-4"/>
 </button>
 ) : null}
 </div>
 )
}
